import React from 'react'
import SelectInput from '@/modules/base/components/inputs/select-input/select-input'
import { TenantCollaboratorsService } from '@/modules/tenant/tenantCollaboratorsService'
import lang from '@/lang'
import { useSelector } from 'react-redux'
import { dispatch } from '@/redux/ducks/lists'
import PropTypes from 'prop-types'
import { makeStyles, useTheme } from '@mui/styles'
import { setCollaboratorsData } from '@/redux/ducks/document/list'
import Highlighter from 'react-highlight-words'

const useStyles = makeStyles(() => ({
  label: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  highlightClassName: {
    fontWeight: 'bold',
    backgroundColor: 'unset',
  },
}))

const selectStyles = () => ({
  valueContainer: (styles) => ({
    ...styles,
    display: 'flex',
  }),
  indicatorsContainer: (styles) => ({
    ...styles,
    maxHeight: '100%',
  }),
})

const fetchCollaborators = async(ownCompanyLabel, hideInternal, useTenantId, hideWithoutDocumentAccess, hideWithoutActiveSubscription, search) => {
  try {
    const { data } = await TenantCollaboratorsService.getCollaborators()
    const filteredCollaborators = data.filter((collaborator) => {
      if (hideWithoutDocumentAccess && !collaborator.hasDocumentAccess) {
        return false
      }
      if (hideWithoutActiveSubscription && !collaborator.hasActiveSubscription) {
        return false
      }
      return true
    })

    const collaborators = filteredCollaborators.map((collaborator) => {
      const { companyAddress, tenantCollaborations, senderEmail, hasUnseenFiles, hasActiveSubscription } = collaborator
      let label = tenantCollaborations?.name || companyAddress.companyName || senderEmail
      if (tenantCollaborations?.clientNumber) label = `${label} (${tenantCollaborations.clientNumber})`
      const id = useTenantId ? collaborator.id : tenantCollaborations.id
      dispatch(setCollaboratorsData(id, hasUnseenFiles))
      return { value: id, label, hasUnseenFiles, hasActiveSubscription, key: id }
    })

    if (!hideInternal) {
      collaborators.unshift({ value: 'internal', label: ownCompanyLabel })
    }

    const options = search
      ? collaborators.filter(collaborator => collaborator.label.toLowerCase().includes(search.toLowerCase()))
      : collaborators

    return { options }
  } catch (error) {
    console.error(error)
  }
}

const CustomOption = (props) => {
  const { data, innerRef, innerProps, isSelected, isFocused, type, selectProps } = props
  const { document: { list: { collaborators } } } = useSelector(state => state)
  const theme = useTheme()
  const classes = useStyles()

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        color: isSelected ? theme.primaryDark : '#23282d',
        backgroundColor: isFocused ? '#f5fafd' : 'transparent',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: '#f5fafd',
        },
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 4,
        height: '45px',
        paddingLeft: type === 'option' ? '8px' : 'unset',
        paddingRight: type === 'option' ? '8px' : 'unset',
        width: '100%',
      }}
      ref={innerRef}
      {...innerProps}
    >
      <Highlighter
        searchWords={[selectProps.inputValue || '']}
        textToHighlight={data.label || ''}
        autoEscape
        highlightClassName={classes.highlightClassName}
        className={classes.label}
      />
      {collaborators[data.value] > 0 && data.value !== 'internal' && (
        <span
          style={{
            borderRadius: '50%',
            background: theme.error,
            border: theme.border,
            height: 10,
            width: 10,
            marginLeft: '2px',
          }}
        ></span>
      )}
    </div>
  )
}
export function CollaborationSelect(props) {
  const {
    onChange,
    isMulti = false,
    hideInternal = false,
    hideWithoutDocumentAccess = false,
    hideWithoutActiveSubscription = false,
    useTenantId = false,
    useDefault = false,
    ...restProps
  } = props
  const classes = useStyles()
  const {
    user: { info: { activeTenant: { companyName } } },
    tenant: { info: { subscriptions } },
  } = useSelector(state => state)
  const isTaxConsultant = subscriptions?.some(s => s.isTaxConsultant)
  const defaultCompanyName = isTaxConsultant ? lang.selectInput.user.taxOffice : lang.selectInput.user.ownCompany
  const ownCompanyLabel = companyName?.length ? `${companyName} (${lang.selectInput.user.taxOffice})` : defaultCompanyName

  const handleCollaboratorChange = (option) => {
    onChange && onChange(option)
  }

  const selectOptions = {
    infiniteScroll: true,
    allowCreate: false,
    styles: selectStyles(),
    customComponents: {
      Option: (props) => <CustomOption {...props} />,
      SingleValue: (props) => <CustomOption {...props} />,
    },
    customSelectOptions: {
      loadOptions: (search) => fetchCollaborators(ownCompanyLabel, hideInternal, useTenantId, hideWithoutDocumentAccess, hideWithoutActiveSubscription, search),
      loadingMessage: lang.selectInput.tenant.loadingMessage,
      isClearable: false,
      escapeClearsValue: true,
      hideSelectedOptions: false,
      placeholder: lang.selectInput.tenant.placeholder(false),
      defaultMenuIsOpen: false,
      defaultOptions: true,
      isMulti,
      useDefault: true,
    },
    useFormik: false,
  }

  if (useDefault) {
    selectOptions.customSelectOptions.useDefault = true
    selectOptions.customSelectOptions.defaultValue = { value: 'internal', label: ownCompanyLabel }
  }

  return (
    <SelectInput
      {...restProps}
      {...selectOptions}
      handleChange={handleCollaboratorChange}
    />
  )
}

CollaborationSelect.propTypes = {
  onChange: PropTypes.func.isRequired,
  isMulti: PropTypes.bool,
  hideInternal: PropTypes.bool,
  hideWithoutDocumentAccess: PropTypes.bool,
  hideWithoutActiveSubscription: PropTypes.bool,
  useTenantId: PropTypes.bool,
  useDefault: PropTypes.bool,
}
